import { useState } from "react";
import { Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";

export const ShareApplicationButton = ({ onShared }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [match, setMatch] = useState(null);

  const lookup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/customer/share/lookup", { query });
      setMatch(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/customer/share", {
        query,
        origin_url: window.location.origin,
      });
      toast.success(`Your credit application is now shared with ${data.vendor_name}`);
      setOpen(false);
      setQuery("");
      setMatch(null);
      onShared?.();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          data-testid="share-application-button"
          className="rounded-full bg-blue-600 px-5 font-semibold transition-colors duration-200 hover:bg-blue-700 active:scale-95"
        >
          <Share2 className="mr-2 h-4 w-4" /> Share with a Vendor/Supplier
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-extrabold tracking-tight">
            Share your credit application
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            Send your existing application and trade references to another Vendor/Supplier — no
            waiting to be asked, and no forms to fill in again.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Vendor/Supplier name or email
            </Label>
            <div className="mt-2 flex gap-2">
              <Input
                value={query}
                placeholder="Acme Distributors  ·  or  ap@acme.com"
                onChange={(e) => {
                  setQuery(e.target.value);
                  setMatch(null);
                }}
                data-testid="share-vendor-input"
                className="h-11 border-slate-300 focus-visible:ring-blue-600"
              />
              <Button
                type="button"
                variant="outline"
                disabled={busy || query.trim().length < 3}
                onClick={lookup}
                data-testid="share-vendor-find-button"
                className="h-11 shrink-0 rounded-full border-slate-300 px-5 font-semibold"
              >
                {busy ? "Looking…" : "Find"}
              </Button>
            </div>
            <p className="mt-2 text-xs text-slate-500">
              Use the exact business name on their ConfirmTerms account, or the email they sign in
              with.
            </p>
          </div>

          {match && !match.found && (
            <p className="text-sm font-medium text-red-600" data-testid="share-vendor-not-found">
              No Vendor/Supplier found with that name or email.
            </p>
          )}

          {match?.found && (
            <div
              className="rounded-lg border border-blue-300 bg-blue-50 p-4"
              data-testid="share-vendor-found"
            >
              <p className="text-sm font-semibold text-blue-900">{match.vendor_name}</p>
              {match.already_shared ? (
                <p className="mt-1 text-sm text-blue-800" data-testid="share-vendor-already">
                  They already have access to your credit application.
                </p>
              ) : (
                <p className="mt-1 text-sm text-blue-800">
                  Sharing gives them your credit application and its trade references, kept up to
                  date automatically.
                </p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              disabled={busy || !match?.found || match?.already_shared}
              onClick={share}
              data-testid="share-vendor-confirm-button"
              className="h-11 w-full rounded-md bg-blue-600 font-semibold hover:bg-blue-700"
            >
              {busy ? "Sharing…" : "Share my application"}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};
